import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SocialMedia from '../../components/SocialMedia/SocialMedia';

import { SignInContext } from '../../data/SignIn/SignInContext';
import { useControllerFlowStepsContext } from '../../hooks/ControllerFlowStepsContext';
import { deleteCookie } from '../../services/cookieController';
import {
  pushGtmEvent,
  setOriginDomainCustom,
  shouldOpenMenuAccount
} from '../../utils/GenericFunctions';
import './SignIn.scss';
import StepHeader from '../../components/Wizard/StepHeader';
import GoogleOneTap from '../../components/SocialMedia/GoogleOneTap';
import Button from '../../components/Button/Button';
import ButtonLabel from '../../components/Button/ButtonLabel';
import Alert from '../../components/Alert';
import FormField from '../../components/FormField/FormField';
import {
  CODE_ACCOUNT_WITH_EMAIL_NOT_FOUND,
  CODE_CREDENTIAL_WITH_NO_PASS,
  CODE_EMAIL_NOT_SKIP_VALIDATE,
  CODE_NOT_ACCOUNT,
  CTV_SUCCESS_PAGE_PATH,
  PASS_MAX_LENGTH
} from '../../data/Constants';
import { passwordMask } from '../../utils/Masks';
import { useAuthContext } from '../../hooks/AuthContext';
import { AccountStatusEnum } from '../../enums/AccountStatusEnum';
import { signIn } from '../../services/user';
import { UNEXPECT_ERROR_MESSAGE } from '../../data/ErrorMessages';
import {
  deleteRefreshTokenSession,
  deleteTokenSession
} from '../../utils/auth';
import RedirectSignUp from '../SignInWizard/RedirectSignUp';
import Separator from '../../components/Separator';

const KEY_SESSION_SOCIAL = process.env.REACT_APP_KEY_SESSION_SOCIAL as string;
const KEY_SESSION_PROVIDER = process.env
  .REACT_APP_KEY_SESSION_PROVIDER as string;
const KEY_SESSION_ORIGIN = process.env.REACT_APP_KEY_SESSION_ORIGIN as string;

export interface SignInProps {
  allowSkipStep?: boolean;
  updateData?: boolean;
  previousPath?: number | null;
  nextPath?: number | null;
  handleChangeStep(e: number): void;
  context: any;
  viewSuccess?: any;
}

export default function SignIn({ context }: SignInProps) {
  const [fieldsValidate, setValueValidateFields] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errorRequest, setErrorRequest] = useState('');

  const {
    setControllerViews,
    getOrigin,
    setStatusAccountUser,
    setAllowSkipValidateEmail,
    ctvCode
  } = useControllerFlowStepsContext();
  const { register, trigger, watch, errors, touchedFields, getValues } =
    useContext(SignInContext);
  const { setNotCreateAccount } = useControllerFlowStepsContext();
  const { originDomainForm } = useContext(SignInContext);
  const { redirectURL, saveUserTokens } = useAuthContext();

  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.clear();
    setControllerViews({});
    setOriginDomainCustom();
    deleteCookie('redirect');
  }, []);

  async function checkValidateFields() {
    return await trigger(['email', 'password']);
  }

  useEffect(() => {
    const subscription = watch((data: any) => {
      setValueValidateFields(data);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  async function subtmiLogin() {
    const response = await checkValidateFields();

    if (response) login();
  }

  useEffect(() => {
    setNotCreateAccount(true);
    if (originDomainForm) {
      const url = `/${originDomainForm}`;
      navigate(url, { replace: true });
    }
  }, [originDomainForm]);

  function redirectToForgetPassword() {
    pushGtmEvent('forgot_password_click');
    navigate(
      getOrigin() == 'videos'
        ? '/videos/recuperar-senha'
        : getOrigin() === 'sbt'
          ? '/sbt/recuperar-senha'
          : '/recuperar-senha'
    );
  }

  async function login() {
    const email = getValues('email');
    const password = getValues('password');
    setLoading(true);
    setErrorRequest('');
    try {
      const resp = await signIn(email, password);
      saveUserTokens(resp);
      setStatusAccountUser(resp);
      if (resp?.status !== AccountStatusEnum.ACTIVE) {
        const url = `${
          !getOrigin() ? '' : '/' + getOrigin()
        }/reativar-conta?email=${encodeURIComponent(email)}`;
        navigate(url);
      } else {
        pushGtmEvent('login_success', { method: 'password' });
        if (ctvCode) return navigate(CTV_SUCCESS_PAGE_PATH);
        if (shouldOpenMenuAccount(redirectURL))
          return navigate('/menu-de-conta');
        setControllerViews(resp);
      }
    } catch (error: any) {
      console.log(error);
      if (error?.code === CODE_NOT_ACCOUNT) {
        setErrorRequest('E-mail ou senha não parecem corretos.');
      } else if (error?.code === CODE_EMAIL_NOT_SKIP_VALIDATE) {
        setAllowSkipValidateEmail(false);
        setControllerViews({ emailValidated: false });
      } else if (error?.code === CODE_ACCOUNT_WITH_EMAIL_NOT_FOUND) {
        setErrorRequest('Não encontramos uma Conta SBT para este e-mail.');
      } else if (error?.code === CODE_CREDENTIAL_WITH_NO_PASS) {
        setErrorRequest('E-mail ou senha não parecem corretos.');
      } else {
        setErrorRequest(UNEXPECT_ERROR_MESSAGE);
        deleteTokenSession();
        deleteRefreshTokenSession();
        deleteCookie(KEY_SESSION_SOCIAL);
        deleteCookie(KEY_SESSION_PROVIDER);
        deleteCookie(KEY_SESSION_ORIGIN);
      }
      setLoading(false);
    }
  }

  return (
    <div className="card__section">
      <StepHeader
        title="Vamos entrar?"
        description="Utilize o login social para entrar ou seu e-mail e senha."
      />
      <SocialMedia context={context} />

      <Separator text="ou" />

      <div className="form-group">
        <Alert
          message={errorRequest}
          errors={errors}
          onClose={setErrorRequest}
        />

        <FormField
          name="email"
          // label="e-mail"
          type="email"
          placeholder="Seu e-mail"
          register={register('email')}
          error={errors}
          touchedFields={touchedFields}
          prefix
          iconPrefix="email"
          autocomplete="email"
        />

        <FormField
          name="password"
          // label="senha"
          type="password"
          maxLength={PASS_MAX_LENGTH}
          placeholder="Sua Senha"
          register={register('password')}
          onInput={passwordMask}
          error={errors}
          touchedFields={touchedFields}
          prefix
          iconPrefix="password"
          autocomplete="current-password"
        />

        <a
          className="caption tertiary hover"
          rel="noreferrer"
          onClick={redirectToForgetPassword}
        >
          Esqueceu sua senha?
        </a>
      </div>
      <Button
        id="signin"
        customColors="btn-primary"
        aria-label="Continuar com e-mail e senha"
        onClick={subtmiLogin}
        disabled={
          loading ||
          !fieldsValidate.email.length ||
          !fieldsValidate.password.length ||
          fieldsValidate.password.length < 6 ||
          !!errors.email ||
          !!errors.password
        }
      >
        <ButtonLabel>Entrar</ButtonLabel>
      </Button>
      <RedirectSignUp context={context} />
      <GoogleOneTap />
    </div>
  );
}
